/**
 * SeasonalityModeView Component
 *
 * Displays ALL projection metrics with monthly seasonality breakdown.
 * Shows exactly the same metrics as the projection input view but with
 * Jan-Dec columns instead of per-year YTD columns.
 *
 * Weighted seasonality: 50% × newest + 30% × middle + 20% × oldest of the
 * seasonalityYears (e.g. 2025/2024/2023 while the 2027 spread is provisional).
 */

import React, { useState, useMemo } from 'react';
import { MonthlyTotals, createEmptyMonthlyTotals, addMonthlyTotals } from '../../types';
import { DOC_TEMPLATE } from '../../config/docTemplate';
import { SeasonalityModeViewProps } from './seasonalityModeTypes';
import { applySeasonality } from '../../utils/seasonalityCalculation';
import {
  exportProjectionsToExcel,
  buildProjectionsExportData,
  DepartmentExportData,
} from '../../utils/projectionsExport';
import { buildConsolidatedExportData } from '../../utils/consolidatedExportBuilder';
import { useSeasonalityModeData } from './useSeasonalityModeData';
import { useDepartmentAggregates } from './useDepartmentAggregates';
import { buildLocationExportDepartments } from './seasonalityExportBuilder';
import { SeasonalityLocationSummary } from './SeasonalityLocationSummary';
import { SeasonalityDepartmentSection } from './SeasonalityDepartmentSection';

// Re-export DepartmentAggregates for consumers that import from this file
export type { DepartmentAggregates } from './seasonalityModeTypes';

export const SeasonalityModeView: React.FC<SeasonalityModeViewProps> = ({
  inputs,
  calculations,
  localInputs,
  historicalYears,
  seasonalityYears,
  seasonalityProvisional,
  historicalDataByYear,
  targetYear,
  locationName,
  formatCurrency,
  getProjectionValue,
  getTotalProjectedUnits,
  getCalculatedGross,
  getCategoryAccountExpenses,
  getServiceDeptProjections,
  onAggregatesCalculated,
  comparisonMode,
  actualData,
  isConsolidated = false,
  expensesByAccount,
  viewData,
  assignedDepartmentIds,
  monthlyAdjustment,
}) => {
  // Department filtering for DepartmentManagers
  const isDeptRestricted = assignedDepartmentIds && assignedDepartmentIds.length > 0;
  const visibleDepartments = useMemo(() => {
    if (!isDeptRestricted) return DOC_TEMPLATE;
    return DOC_TEMPLATE.filter(dept => assignedDepartmentIds!.includes(dept.name));
  }, [assignedDepartmentIds, isDeptRestricted]);

  // Helper to get department data from viewData when available
  // Returns null if viewData not available, allowing fallback to local calculation
  const getViewDataDepartment = (deptName: string) => {
    if (!viewData?.departments) return null;
    return viewData.departments.find(d => d.department === deptName) || null;
  };

  // Track which months have actual columns visible (clickable toggle)
  const [visibleActualMonths, setVisibleActualMonths] = useState<Set<number>>(new Set());

  // Track which sections have comparison charts expanded
  const [expandedCharts, setExpandedCharts] = useState<Set<string>>(new Set());

  // Toggle chart visibility for a section
  const toggleChart = (sectionKey: string) => {
    setExpandedCharts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  };

  // Toggle visibility of actual column for a month
  const toggleActualMonth = (month: number) => {
    if (!comparisonMode) return;
    setVisibleActualMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(month)) {
        newSet.delete(month);
      } else {
        newSet.add(month);
      }
      return newSet;
    });
  };

  const data = useSeasonalityModeData({
    inputs, calculations, localInputs, historicalYears, seasonalityYears, historicalDataByYear,
    actualData, comparisonMode, isConsolidated, expensesByAccount,
    getProjectionValue, getTotalProjectedUnits, getCalculatedGross, getCategoryAccountExpenses, getServiceDeptProjections,
  });
  const { calculationsMap, getDeptUnitSeasonalityWeights } = data;

  // Track expanded departments and categories
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleDepartment = (deptName: string) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deptName)) {
        newSet.delete(deptName);
      } else {
        newSet.add(deptName);
      }
      return newSet;
    });
  };

  const toggleCategory = (key: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Expand all departments and categories
  const expandAll = () => {
    // Expand all visible departments
    const allDepts = new Set(visibleDepartments.map(d => d.name));
    setExpandedDepartments(allDepts);

    // Expand all categories within each visible department
    const allCategories = new Set<string>();
    visibleDepartments.forEach(dept => {
      dept.expenseCategories.forEach(cat => {
        allCategories.add(`${dept.name}#${cat.name}`);
      });
      // Include service/parts pay-type breakdown if in comparison mode
      if (comparisonMode && (dept.name === 'SERVICE' || dept.name === 'PARTS' || dept.name === 'BODY_SHOP')) {
        allCategories.add(`${dept.name}-GROSS-PAYTYPE`);
      }
    });
    setExpandedCategories(allCategories);
  };

  // Collapse all departments and categories
  const collapseAll = () => {
    setExpandedDepartments(new Set());
    setExpandedCategories(new Set());
  };

  // Print handler
  const handlePrint = () => {
    // Add class to body for print-specific styles
    document.body.classList.add('printing-projections');

    // Trigger print
    window.print();

    // Remove class after print dialog closes
    setTimeout(() => {
      document.body.classList.remove('printing-projections');
    }, 100);
  };

  // Calculate location-level ACTUAL totals by summing department actuals
  // Used for comparison mode in the Location Summary section
  const locationActuals = useMemo(() => {
    if (!comparisonMode || !actualData?.docGross) {
      return null;
    }

    let totalGross = 0;
    let totalExpenses = 0;
    let monthlyGross = createEmptyMonthlyTotals();
    let monthlyExpenses = createEmptyMonthlyTotals();

    // Sum ALL department actual data directly from docGross
    // This includes F_AND_I_NEW and F_AND_I_USED which are separate departments in the data
    // but need to be included in the location total
    // Include OTHERS in the total (user preference)
    for (const dept of actualData.docGross) {

      // Get gross - try both gross and monthlyGross fields
      const grossMonthly = dept.gross || dept.monthlyGross || createEmptyMonthlyTotals();
      const grossYtd = dept.ytdGross || 0;

      totalGross += grossYtd;
      monthlyGross = addMonthlyTotals(monthlyGross, grossMonthly);

      // Add expenses if available (F&I departments typically don't have expenses)
      if (dept.totalExpenses) {
        const expenseYtd = dept.ytdTotalExpenses || 0;
        totalExpenses += expenseYtd;
        monthlyExpenses = addMonthlyTotals(monthlyExpenses, dept.totalExpenses);
      }
    }

    // Calculate operating profit as gross - expenses
    const operatingProfit = totalGross - totalExpenses;
    const monthlyProfit = createEmptyMonthlyTotals();
    for (let m = 1; m <= 12; m++) {
      const key = `month${m}` as keyof MonthlyTotals;
      monthlyProfit[key] = monthlyGross[key] - monthlyExpenses[key];
    }

    return {
      totalGross,
      totalExpenses,
      operatingProfit,
      monthlyGross,
      monthlyExpenses,
      monthlyProfit,
    };
  }, [comparisonMode, actualData]);

  // Calculate grand totals for location summary using BOTTOM-UP aggregation
  // This ensures location-level totals are consistent with department-level totals
  // IMPORTANT: Sum stored monthly values from each department to match Forecast page "ALL" calculation
  //
  // For CONSOLIDATED view: Use pre-calculated values from calculations instead of recalculating
  // This is necessary because expense percentages can't be meaningfully aggregated across locations
  const locationTotals = useMemo(() => {
    // viewData is required - no fallback calculation
    if (!viewData?.locationSummary) {
      console.error('SeasonalityModeView: viewData.locationSummary is required but not available');
      return {
        totalGross: 0,
        totalExpenses: 0,
        operatingProfit: 0,
        monthlyGross: createEmptyMonthlyTotals(),
        monthlyExpenses: createEmptyMonthlyTotals(),
        monthlyProfit: createEmptyMonthlyTotals(),
      };
    }

    const summary = viewData.locationSummary;
    return {
      totalGross: summary.projectedGross,
      totalExpenses: summary.projectedExpenses,
      operatingProfit: summary.projectedProfit,
      monthlyGross: summary.monthlyGross,
      monthlyExpenses: summary.monthlyExpenses,
      monthlyProfit: summary.monthlyProfit,
    };
  }, [viewData]);

  const departmentAggregates = useDepartmentAggregates({
    visibleDepartments, data, getCalculatedGross, getProjectionValue, getTotalProjectedUnits,
  });

  // Track previous aggregates to prevent infinite loops
  // (The useMemo dependencies include non-memoized functions that change every render)
  const prevAggregatesRef = React.useRef<string>('');

  // Call the callback with calculated aggregates only when values actually change
  React.useEffect(() => {
    if (onAggregatesCalculated) {
      const aggregatesJson = JSON.stringify(departmentAggregates);
      if (aggregatesJson !== prevAggregatesRef.current) {
        prevAggregatesRef.current = aggregatesJson;
        onAggregatesCalculated(departmentAggregates);
      }
    }
  }, [departmentAggregates, onAggregatesCalculated]);

  // For consolidated view, we have calculations but no inputs
  // For regular locations, we need inputs to have data
  const hasProjectionData = isConsolidated
    ? (calculations && calculations.length > 0)
    : inputs.length > 0;

  // Excel export handler - builds export data matching what's displayed on screen
  const handleExportExcel = () => {
    const departmentData: DepartmentExportData[] = isConsolidated && viewData
      // Consolidated view: use pre-calculated viewData + expensesByAccount
      // This matches exactly what the department sections display on screen
      ? buildConsolidatedExportData(
          viewData, expensesByAccount, calculationsMap,
          getTotalProjectedUnits, getDeptUnitSeasonalityWeights, applySeasonality,
        )
      : buildLocationExportDepartments({ visibleDepartments, data, departmentAggregates, isConsolidated });

    // Build and export
    const exportData = buildProjectionsExportData(
      locationName,
      targetYear,
      locationTotals,
      departmentData
    );
    exportProjectionsToExcel(exportData);
  };

  if (!hasProjectionData) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-500">
          No projection data. Enter and save projections in Historical mode first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {seasonalityProvisional && !isConsolidated && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 no-print">
          <strong>Provisional monthly spread</strong> ({[...seasonalityYears].reverse().join(', ')}). The final
          spread is recalculated Jan {10}, {targetYear} from {targetYear - 3}&ndash;{targetYear - 1} after December
          closes. Annual totals you enter are not changed.
        </div>
      )}
      {/* Print Toolbar */}
      <div className="flex justify-end gap-2 no-print">
        <button
          onClick={collapseAll}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Collapse All
        </button>
        <button
          onClick={expandAll}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Expand All
        </button>
        <button
          onClick={handlePrint}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-1"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print
        </button>
        <button
          onClick={handleExportExcel}
          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center gap-1"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export to Excel
        </button>
      </div>

      {/* Print Container - wraps all printable content */}
      <div id="projections-print-container">
        {/* Location Summary - hidden for dept-restricted users */}
        {!isDeptRestricted && (
          <SeasonalityLocationSummary
            targetYear={targetYear}
            formatCurrency={formatCurrency}
            getTotalProjectedUnits={getTotalProjectedUnits}
            comparisonMode={comparisonMode || false}
            actualData={actualData}
            data={data}
            locationTotals={locationTotals}
            locationActuals={locationActuals}
            visibleActualMonths={visibleActualMonths}
            toggleActualMonth={toggleActualMonth}
            expandedCharts={expandedCharts}
            toggleChart={toggleChart}
          />
        )}

        {/* Department Details */}
        {visibleDepartments.map(dept => (
          <SeasonalityDepartmentSection
            key={dept.name}
            dept={dept}
            targetYear={targetYear}
            formatCurrency={formatCurrency}
            actualData={actualData}
            expensesByAccount={expensesByAccount}
            getProjectionValue={getProjectionValue}
            getTotalProjectedUnits={getTotalProjectedUnits}
            getCalculatedGross={getCalculatedGross}
            getServiceDeptProjections={getServiceDeptProjections}
            comparisonMode={comparisonMode || false}
            isConsolidated={isConsolidated}
            data={data}
            ui={{
              expandedDepartments, expandedCategories, toggleDepartment, toggleCategory,
              visibleActualMonths, toggleActualMonth, expandedCharts, toggleChart,
            }}
            getViewDataDepartment={getViewDataDepartment}
            calculations={calculations}
            monthlyAdjustment={monthlyAdjustment}
          />
        ))}
      </div>
    </div>
  );
};
